import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { useSetKimThreadNotifications } from '@/api/mutations/useSetKimThreadNotifications';
import { Icon } from '@/new/components/icon';
import { Label } from '@/new/components/shadcn/label';
import { Switch } from '@/new/components/shadcn/switch';

interface ComposingMessageProps {
  enableNotifications: boolean;
  // Only true when the viewer owns the thread; the notifications switch is hidden otherwise.
  canToggleNotifications: boolean;
}

export function ComposingMessage({ enableNotifications, canToggleNotifications }: ComposingMessageProps) {
  const { matterId, subsetId, threadId } = useParams();
  const { mutate: setThreadNotifications } = useSetKimThreadNotifications(matterId, subsetId);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="relative mx-auto my-4 max-w-3xl"
    >
      <div className="relative z-10 flex items-center gap-2 rounded-lg border bg-white px-3 py-2.5 text-xs text-gray-600 shadow-lg/1">
        <Icon className="lucide--loader animate-spin text-teal-500 ease-in-out" />
        <span>Thinking deeply. It may take longer to generate a response.</span>
      </div>

      {canToggleNotifications && threadId && (
        <div className="-mt-1.5 flex items-center gap-2 rounded-b-xl bg-gray-200/60 p-3 px-2.5 pb-2.25">
          <Switch
            id="kim-thread-notifications"
            checked={enableNotifications}
            onCheckedChange={(value) => setThreadNotifications({ threadId, enableNotifications: value })}
            className="scale-80 data-[state=unchecked]:bg-gray-300"
          />
          <Label
            htmlFor="kim-thread-notifications"
            className="m-0 cursor-pointer text-xs font-normal text-gray-700 duration-150 hover:text-gray-900"
          >
            Send me an email notification when this answer completes
          </Label>
        </div>
      )}
    </motion.div>
  );
}
